import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from "react";
import type { AnnotationStroke } from "../../../shared/types";

const PEN = ["#f6f1e8", "#c3b4ff", "#2ee6c7", "#ff6d7a"];
const MARK = ["#f0b429", "#2ee6c7", "#8b6cff", "#ff6d7a"];

export type DrawTool = "pan" | "pen" | "highlight";

export function colorsFor(tool: DrawTool): string[] {
  return tool === "highlight" ? MARK : PEN;
}

export function paintStrokes(canvas: HTMLCanvasElement, strokes: AnnotationStroke[]): void {
  const context = canvas.getContext("2d");
  if (!context) return;
  context.clearRect(0, 0, canvas.width, canvas.height);
  for (const stroke of strokes) {
    context.beginPath();
    context.strokeStyle = stroke.color;
    context.globalAlpha = stroke.tool === "highlight" ? 0.38 : 0.95;
    context.lineWidth = stroke.width * (canvas.width / Math.max(canvas.clientWidth, 1));
    context.lineCap = "round";
    context.lineJoin = "round";
    stroke.points.forEach((point, index) => {
      const x = point.x * canvas.width;
      const y = point.y * canvas.height;
      if (index === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    });
    if (stroke.points.length === 1) {
      const point = stroke.points[0];
      if (point) context.arc(point.x * canvas.width, point.y * canvas.height, Math.max(stroke.width, 1.5), 0, Math.PI * 2);
    }
    context.stroke();
  }
  context.globalAlpha = 1;
}

function pointOf(canvas: HTMLCanvasElement, event: ReactPointerEvent<HTMLCanvasElement>): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const x = rect.width === 0 ? 0 : (event.clientX - rect.left) / rect.width;
  const y = rect.height === 0 ? 0 : (event.clientY - rect.top) / rect.height;
  return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) };
}

export function AnnotationLayer({
  strokes,
  tool,
  color,
  width,
  onCommit,
}: {
  strokes: AnnotationStroke[];
  tool: DrawTool;
  color: string;
  width: number;
  onCommit: (strokes: AnnotationStroke[]) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<AnnotationStroke | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const frame = canvas?.parentElement;
    if (!canvas || !frame) return;
    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const nextWidth = Math.max(1, Math.floor(frame.clientWidth * ratio));
      const nextHeight = Math.max(1, Math.floor(frame.clientHeight * ratio));
      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
      }
      paintStrokes(canvas, drawing.current ? [...strokes, drawing.current] : strokes);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [strokes]);

  function pointer(event: ReactPointerEvent<HTMLCanvasElement>, phase: "down" | "move" | "up") {
    if (tool === "pan") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = pointOf(canvas, event);
    if (phase === "down") {
      event.preventDefault();
      try { canvas.setPointerCapture(event.pointerId); } catch { /* capture is optional for the stroke */ }
      drawing.current = {
        id: crypto.randomUUID(),
        tool: tool === "highlight" ? "highlight" : "pen",
        color,
        width,
        points: [point],
      };
      paintStrokes(canvas, [...strokes, drawing.current]);
      return;
    }
    if (!drawing.current) return;
    if (phase === "move") {
      if (drawing.current.points.length >= 5000) return;
      drawing.current = { ...drawing.current, points: [...drawing.current.points, point] };
      paintStrokes(canvas, [...strokes, drawing.current]);
      return;
    }
    const finished = drawing.current;
    drawing.current = null;
    onCommit([...strokes, finished]);
  }

  return (
    <canvas
      ref={canvasRef}
      className={tool === "pan" ? "ink-layer idle" : "ink-layer drawing"}
      aria-label="Chart annotations"
      onPointerDown={(event) => pointer(event, "down")}
      onPointerMove={(event) => pointer(event, "move")}
      onPointerUp={(event) => pointer(event, "up")}
      onPointerCancel={(event) => pointer(event, "up")}
    />
  );
}
